import { CompleteCallback, sound, Sound } from "@pixi/sound";
import { gsap } from "gsap";
import { AudioVO } from "@/api/rhApi.ts";
import { CDN } from "@/utils/config.ts";
import { DatGuiManager } from "./DatGuiManager.ts";

/**
 * Options for playing a sound
 */
interface PlaySoundOptions {
    /**
     * Callback when the sound has completed its playback
     */
    completeCallback?: CompleteCallback;
    /**
     * Ignore if the sound is already playing
     */
    ignorePlaying?: boolean;
    /**
     * Stop all sounds before playing
     */
    stopSounds?: boolean;
}

export default class SoundManager {
    //--------------------------------------------------------------------------
    //
    //  Properties
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private static _instance: SoundManager = new SoundManager();
    public lastSoundAlias: string = "";

    public get isPlaying(): boolean {
        return sound.isPlaying();
    }

    //--------------------------------------------------------------------------
    //
    //  Constructor
    //
    //--------------------------------------------------------------------------

    private constructor() {
        if (SoundManager._instance) {
            throw new Error("Error: Instantiation failed: Use SoundManager.getInstance() instead of new.");
        }
        SoundManager._instance = this;
        sound.disableAutoPause = true; // We will manage the pausing of sounds ourselves

        // Uncomment this block if you want to add sound controls to the dat.gui
        /*const folder = DatGuiManager.instance.gui.addFolder("Sound");
        folder.add(sound, "speedAll").min(0.1).max(10).step(0.1).setValue(1);
        folder.add(sound.context, "togglePause");*/
    }

    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private generateAlias(url: string): string {
        const alias = url.substring(url.lastIndexOf("/") + 1, url.lastIndexOf("."));
        this.lastSoundAlias = alias;
        return alias;
    }

    private onFadeSoundComplete = (alias: string): void => {
        const soundObject: Sound = sound.find(alias);
        if (soundObject.volume == 0) {
            this.stopSound(alias);
        }
    };

    //----------------------------------
    //  Public:
    //----------------------------------

    public static getInstance(): SoundManager {
        return SoundManager._instance;
    }

    public fadeSound(url: string, startVolume = 0, endVolume = 1, duration = 2, loop = true, completeCallback?: CompleteCallback): void {
        const alias = this.generateAlias(url);
        let soundObject: Sound = sound.find(alias);
        if (soundObject) {
            if (soundObject.isPlaying) {
                startVolume = soundObject.volume;
            } else {
                sound.play(alias, { loop: loop, complete: completeCallback });
            }
        } else {
            sound.add(alias, url);
            soundObject = sound.find(alias);
            sound.play(alias, { loop: loop, complete: completeCallback });
        }
        if (startVolume == null) {
            startVolume = soundObject.volume;
        }
        gsap.fromTo(soundObject, { volume: startVolume }, { volume: endVolume, duration: duration, onComplete: this.onFadeSoundComplete, onCompleteParams: [alias] });
    }

    public loopSound(url: string): void {
        const alias = this.generateAlias(url);
        const soundObject: Sound = sound.find(alias);
        if (soundObject) {
            if (soundObject.isPlaying) {
                return;
            } else {
                sound.play(alias, { loop: true });
            }
        } else {
            sound.add(alias, url);
            sound.play(alias, { loop: true });
        }
    }

    private getSoundUrl(url: string | AudioVO): string {
        if (typeof url === "string") {
            return url;
        } else {
            return `${CDN}audio/student/${url.type}/${url.id}.mp3`; // adjust this line based on how you can get the url from AudioVO object
        }
    }

    private isUrlAlias(url: string): boolean {
        return !url.includes("://");
    }

    private playExistingSound(alias: string, completeCallback?: CompleteCallback): Promise<Sound> {
        return new Promise((resolve) => {
            const soundObject: Sound = sound.find(alias);
            sound.play(alias, {
                complete: () => {
                    if (completeCallback) {
                        completeCallback(soundObject);
                    }
                    resolve(soundObject);
                },
            });
        });
    }

    private addAndPlaySound(alias: string, soundUrl: string, completeCallback?: CompleteCallback): Promise<Sound> {
        return new Promise((resolve) => {
            sound.add(alias, soundUrl);
            sound.play(alias, {
                complete: () => {
                    const soundObject: Sound = sound.find(alias);
                    if (completeCallback) {
                        completeCallback(soundObject);
                    }
                    resolve(soundObject);
                },
            });
        });
    }

    public async playBlankSound(): Promise<void> {
        await this.playSound(`${CDN}/audio/student/blank.mp3`);
    }

    /**
     * @deprecated Please use the new version of the function with options
     */
    public async playSound(url: string | AudioVO, completeCallback: CompleteCallback, ignorePlaying?: boolean): Promise<Sound>;
    /**
     * Play a sound
     * @param url
     * @param options
     */
    public async playSound(url: string | AudioVO, options?: PlaySoundOptions): Promise<Sound>;
    public async playSound(url: string | AudioVO, optionsOrCallback?: CompleteCallback | PlaySoundOptions, ignorePlaying: boolean = false): Promise<Sound> {
        let options: PlaySoundOptions;
        if (typeof optionsOrCallback === "function") {
            // If the second argument is a function, treat it as the old version of the function
            options = {
                completeCallback: optionsOrCallback,
                ignorePlaying: ignorePlaying,
            };
        } else {
            // Otherwise, treat it as the new version of the function
            options = optionsOrCallback || {};
        }

        const soundUrl = this.getSoundUrl(url);
        const alias = this.isUrlAlias(soundUrl) ? soundUrl : this.generateAlias(soundUrl);
        this.lastSoundAlias = alias;
        const soundExists = sound.exists(alias);

        if (options.stopSounds) this.stopAllSounds();

        if (soundExists) {
            const soundObject: Sound = sound.find(alias);
            if (soundObject.isPlaying && !options.ignorePlaying) {
                return;
            } else {
                return this.playExistingSound(alias, options.completeCallback);
            }
        } else {
            return this.addAndPlaySound(alias, soundUrl, options.completeCallback);
        }
    }

    public async playSoundsInSequence(urls: (string | AudioVO)[], completeCallback?: CompleteCallback, stopSounds = false): Promise<void> {
        let soundObject: Sound | undefined;
        for (const url of urls) {
            soundObject = await this.playSound(url, { stopSounds: stopSounds });
        }
        if (completeCallback && soundObject) {
            completeCallback(soundObject);
        }
    }

    public removeAllSounds(): void {
        sound.removeAll();
    }

    public removeSound(alias: string): void {
        if (sound.find(alias)) {
            sound.remove(alias);
        }
    }

    public stopAllSounds(): void {
        sound.stopAll();
    }

    public stopLastSound(): void {
        this.stopSound(this.lastSoundAlias);
    }

    public stopSound(url: string): void {
        if (sound.exists(url)) {
            sound.stop(url);
            return;
        }

        const alias = url.substring(url.lastIndexOf("/") + 1, url.lastIndexOf("."));
        if (sound.find(alias)) {
            sound.stop(alias);
        }
    }

    public playByAlias(alias: string, onComplete: () => void) {
        sound.play(alias, { complete: onComplete });
    }
}
